import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";

import {
  isCodeStep,
  loginSchema,
  type LoginInput,
  type LoginResult,
  type SessionUser,
} from "@devengo/shared";

import { Button, ErrorBox, Field, Input, Notice } from "../components/ui";
import { api } from "../lib/api";
import { useBranding } from "../lib/session";

type Step = { kind: "password" } | { kind: "code"; email: string } | { kind: "forgot" };

/** The login: password, then the code mailed to the person (plan de seguridad, Fase 2). */
export function LoginPage() {
  const [step, setStep] = useState<Step>({ kind: "password" });
  return (
    <AuthFrame>
      {step.kind === "password" ? (
        <PasswordStep
          onCode={(email) => setStep({ kind: "code", email })}
          onForgot={() => setStep({ kind: "forgot" })}
        />
      ) : null}
      {step.kind === "code" ? (
        <CodeStep email={step.email} onRestart={() => setStep({ kind: "password" })} />
      ) : null}
      {step.kind === "forgot" ? <ForgotStep onBack={() => setStep({ kind: "password" })} /> : null}
    </AuthFrame>
  );
}

/** The studio's card around every screen of the login. */
export function AuthFrame({ children }: { children: ReactNode }) {
  const branding = useBranding();
  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-tint p-6">
      <div className="w-full max-w-sm rounded-xl border border-brand-line bg-white p-6 shadow-sm">
        <div className="mb-5 border-b-4 border-brand-primary pb-3">
          <h1 className="text-xl font-semibold text-brand-primary">{branding.displayName}</h1>
          <p className="text-sm text-brand-muted">Ingresá con tu usuario del estudio.</p>
        </div>
        {children}
        {branding.contact.email || branding.contact.phone ? (
          <p className="mt-5 text-center text-xs text-brand-muted">
            {[branding.contact.address, branding.contact.phone, branding.contact.email]
              .filter(Boolean)
              .join(" · ")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Puts the new session in place; whatever a previous user saw must not reach this one. */
export function useSignedIn() {
  const client = useQueryClient();
  return (user: SessionUser) => {
    // A session can end without a logout (it expired); whatever the previous user saw must not
    // reach the next one, who may have a narrower role (Codex 015 M1 review).
    client.removeQueries({ predicate: (query) => query.queryKey[0] !== "session" });
    client.setQueryData(["session"], user);
  };
}

function PasswordStep({
  onCode,
  onForgot,
}: {
  onCode: (email: string) => void;
  onForgot: () => void;
}) {
  const signedIn = useSignedIn();
  const form = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });
  const login = useMutation({
    mutationFn: (input: LoginInput) => api.post<LoginResult>("/api/auth/login", input),
    onSuccess: (result) => (isCodeStep(result) ? onCode(result.email) : signedIn(result)),
  });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={form.handleSubmit((values) => login.mutate(values))}
    >
      <Field label="Correo" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="username" autoFocus {...form.register("email")} />
      </Field>
      <Field label="Clave" error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="current-password" {...form.register("password")} />
      </Field>
      <ErrorBox error={login.error} />
      <Button type="submit" disabled={login.isPending} className="mt-1">
        {login.isPending ? "Ingresando…" : "Ingresar"}
      </Button>
      <button type="button" className="text-sm text-brand-primary underline" onClick={onForgot}>
        Olvidé mi clave
      </button>
    </form>
  );
}

function CodeStep({ email, onRestart }: { email: string; onRestart: () => void }) {
  const signedIn = useSignedIn();
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [resent, setResent] = useState<string | null>(null);
  const check = useMutation({
    mutationFn: () => api.post<SessionUser>("/api/auth/login/code", { code, remember }),
    onSuccess: signedIn,
  });
  const resend = useMutation({
    mutationFn: () => api.post<{ email: string }>("/api/auth/login/resend"),
    onSuccess: (result) => setResent(`Te mandamos otro código a ${result.email}.`),
  });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        check.mutate();
      }}
    >
      <p className="text-sm">
        Te mandamos un código de 6 números a <strong>{email}</strong>. Vence en 10 minutos.
      </p>
      <Field label="Código">
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
      </Field>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={remember}
          onChange={(e) => setRemember(e.target.checked)}
        />
        <span>
          Recordar este dispositivo por 30 días.{" "}
          <span className="text-brand-muted">No lo tildes en una computadora compartida.</span>
        </span>
      </label>
      <ErrorBox error={check.error ?? resend.error} />
      {resent ? <Notice tone="info">{resent}</Notice> : null}
      <Button type="submit" disabled={code.length !== 6 || check.isPending}>
        {check.isPending ? "Verificando…" : "Ingresar"}
      </Button>
      <div className="flex justify-between text-sm">
        <button
          type="button"
          className="text-brand-primary underline"
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
        >
          Mandar otro código
        </button>
        <button type="button" className="text-brand-muted underline" onClick={onRestart}>
          Volver
        </button>
      </div>
    </form>
  );
}

function ForgotStep({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const forgot = useMutation({
    mutationFn: () => api.post<{ message: string }>("/api/auth/password/forgot", { email }),
  });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        forgot.mutate();
      }}
    >
      <p className="text-sm">
        Escribí tu correo y te mandamos un enlace para elegir una clave nueva.
      </p>
      <Field label="Correo">
        <Input
          type="email"
          autoComplete="username"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>
      <ErrorBox error={forgot.error} />
      {forgot.data ? <Notice tone="success">{forgot.data.message}</Notice> : null}
      <Button type="submit" disabled={!email || forgot.isPending || forgot.isSuccess}>
        Mandarme el enlace
      </Button>
      <button type="button" className="text-sm text-brand-muted underline" onClick={onBack}>
        Volver a ingresar
      </button>
    </form>
  );
}
